import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import maplibregl from 'maplibre-gl';
import { Play, Pause, SkipBack, Loader2, AlertTriangle, Search, X } from 'lucide-react';
import type { FullMapHandle } from './FullMap';
import HighRiskVehiclesList from './HighRiskVehiclesList';
import { useVehicleReplay } from '@/api/hooks/useVehicleReplay';
import { useVehicleSearch } from '@/api/hooks/useVehicleTimeline';
import { decodePolyline } from '@/lib/polyline';
import type { ReplayPoint, ReplayResponse } from '@/types/vehicles';
import type { Severity } from '@/types/alerts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { asSeverity, SEVERITY_BADGE_CLASS, SEVERITY_ORDER, severityMapColor } from '@/lib/severity';
import { cn } from '@/lib/utils';

/**
 * Vehicle movement-replay panel (Maps "Vehicle" tool). Search a registration,
 * load its oldest-first geolocated touch-points, then play forward: a moving
 * blip + growing trail, the map following at street zoom, an info popup, and a
 * pause + light-red blip whenever a stop is potential-fraud. Drives the map
 * imperatively via the shared FullMap handle (same getMap() pattern Maps.tsx
 * uses for routes/heatmaps).
 */

const KIND_COLOUR: Record<string, string> = { fuel: '#1F6FB8', oil: '#A8852C', toll: '#8A4FA0' };
const DEFAULT_COLOUR = '#2563eb';
const TRAIL_SRC = 'fb-replay-trail';
const TRAIL_LAYER = 'fb-replay-trail-line';
const TRAIL_HALO = 'fb-replay-trail-halo';
const STOPS_SRC = 'fb-replay-stops';
const STOPS_LAYER = 'fb-replay-stops-dot';
const STOPS_PULSE = 'fb-replay-stops-pulse';
const SPEEDS = [1, 2, 3, 4] as const;
/** Severities loud enough to earn the pulsing halo on the trail. */
const PULSE_SEVERITIES: Severity[] = ['Critical', 'High'];
const WHY_MAX_CHARS = 140;

function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function truncate(s: string, max = WHY_MAX_CHARS): string {
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

/** A stop is flagged when the replay marked it, or a fraud.alert was linked. */
function isFlagged(p: ReplayPoint): boolean {
  return Boolean(p.potential_fraud || p.is_fraud);
}
/** The stop's alert severity, narrowed to the shared palette's keys. */
function stopSeverity(p: ReplayPoint): Severity | null {
  return asSeverity(p.severity);
}
/**
 * Blip colour: the shared severity palette when the stop carries an alert
 * annotation, the touchpoint-kind colour when it doesn't. A stop flagged by
 * score alone (no linked alert, so no severity) falls to the palette's
 * `Watch` tier rather than borrowing a tier it wasn't assigned — it still
 * reads as flagged through the larger radius.
 */
function stopColour(p: ReplayPoint): string {
  const sev = stopSeverity(p);
  if (sev) return severityMapColor(sev);
  if (isFlagged(p)) return severityMapColor('Watch');
  return KIND_COLOUR[p.kind] ?? DEFAULT_COLOUR;
}
function toDateInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

// Fixed wall-clock seconds to traverse each leg (one stop → the next) per speed.
const SECONDS_PER_LEG: Record<number, number> = { 1: 10, 2: 8, 3: 4, 4: 2 };
const FOLLOW_ZOOM = 13; // pull back to here while driving so the road stays visible

interface ReplayPath {
  path: number[][]; // full road polyline [lon,lat] (all legs concatenated)
  cum: number[]; // cumulative km at each vertex (len = path.length)
  stopKm: number[]; // cumulative km at each stop (len = points.length)
  totalKm: number;
}

/** Approx great-circle km between two [lon,lat] (fine at fleet scale). */
function segKm(a: number[], b: number[]): number {
  const dx = (b[0] - a[0]) * Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180) * 111.32;
  const dy = (b[1] - a[1]) * 110.57;
  return Math.hypot(dx, dy);
}

/**
 * Concatenate the per-leg road geometry (Valhalla polyline6, decoded; straight
 * fallback where a leg is un-routable) into one continuous polyline, plus the
 * cumulative distance at every vertex and the offset of each stop along it.
 * This is what the playhead travels so the vehicle drives along the roads.
 */
function buildReplayPath(replay: ReplayResponse): ReplayPath {
  const pts = replay.points;
  const legs = replay.route_legs;
  if (pts.length === 0) return { path: [], cum: [], stopKm: [], totalKm: 0 };
  const path: number[][] = [[pts[0].lon, pts[0].lat]];
  const stopVertex: number[] = [0];
  for (let k = 0; k < pts.length - 1; k++) {
    const enc = legs?.[k];
    let seg: number[][] = enc ? decodePolyline(enc, 6) : [];
    if (seg.length < 2) {
      seg = [
        [pts[k].lon, pts[k].lat],
        [pts[k + 1].lon, pts[k + 1].lat],
      ];
    }
    seg = seg.slice(1); // drop the shared join vertex
    if (seg.length === 0) seg = [[pts[k + 1].lon, pts[k + 1].lat]];
    for (const c of seg) path.push(c);
    stopVertex.push(path.length - 1);
  }
  const cum: number[] = new Array(path.length);
  cum[0] = 0;
  for (let i = 1; i < path.length; i++) cum[i] = cum[i - 1] + segKm(path[i - 1], path[i]);
  const stopKm = stopVertex.map((v) => cum[v]);
  return { path, cum, stopKm, totalKm: cum[cum.length - 1] };
}

/** First vertex index whose cumulative distance ≥ km (binary search). */
function vertexAtKm(cum: number[], km: number): number {
  if (km <= 0) return 0;
  if (km >= cum[cum.length - 1]) return cum.length - 1;
  let lo = 0;
  let hi = cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] < km) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Interpolated [lon,lat] at distance `km` along the path. */
function posAtKm(rp: ReplayPath, km: number): [number, number] {
  const { path, cum } = rp;
  if (path.length === 0) return [0, 0];
  if (km <= 0) return path[0] as [number, number];
  if (km >= cum[cum.length - 1]) return path[path.length - 1] as [number, number];
  const i = vertexAtKm(cum, km);
  const segStart = cum[i - 1];
  const segEnd = cum[i];
  const t = segEnd > segStart ? (km - segStart) / (segEnd - segStart) : 0;
  const a = path[i - 1];
  const b = path[i];
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/**
 * Data-driven `circle-color`: match the stop's severity against the shared
 * palette, falling back to a nested match on touchpoint kind for unflagged
 * stops (same `match`-built-from-a-constant-map pattern FullMap uses for POI
 * categories). Built once at module load — the palette never changes.
 */
function buildStopColourExpr(): unknown[] {
  const kindMatch: unknown[] = ['match', ['get', 'kind']];
  for (const [kind, colour] of Object.entries(KIND_COLOUR)) kindMatch.push(kind, colour);
  kindMatch.push(DEFAULT_COLOUR);

  const sevMatch: unknown[] = ['match', ['get', 'sev']];
  for (const sev of SEVERITY_ORDER) sevMatch.push(sev, severityMapColor(sev));
  sevMatch.push(kindMatch);
  return sevMatch;
}
const STOP_COLOUR_EXPR = buildStopColourExpr();
const PULSE_FILTER: unknown[] = ['in', ['get', 'sev'], ['literal', PULSE_SEVERITIES]];

/**
 * One GeoJSON point per stop. Only the index and the two styling keys ride
 * on the feature — the popup payload is looked up from `points` by index
 * (a GeoJSON round-trip coerces nested values), same as FraudStoryMap.
 */
function buildStopsGeoJson(points: ReplayPoint[]) {
  return {
    type: 'FeatureCollection' as const,
    features: points.map((p, i) => ({
      type: 'Feature' as const,
      id: i,
      properties: {
        idx: i,
        kind: p.kind,
        sev: stopSeverity(p) ?? '',
        flagged: isFlagged(p),
      },
      geometry: { type: 'Point' as const, coordinates: [p.lon, p.lat] },
    })),
  };
}

function severityChipHtml(sev: Severity): string {
  return (
    `<span style="display:inline-block;padding:0 6px;border-radius:9999px;` +
    `background:${severityMapColor(sev)};color:#ffffff;font-size:10px;` +
    `font-weight:700;letter-spacing:0.02em;vertical-align:1px">${escHtml(sev)}</span>`
  );
}

/**
 * Forensic "WHY this stop is fraudulent" block: `RULE` + a severity chip in
 * the shared palette + the why_summary the /fraud-replay endpoint resolves
 * from the top linked fraud.alert (truncated — the popup is a glance, the
 * case file is the read). ALL fields are HTML-escaped (the summary is
 * LLM-authored text — XSS surface). Falls back to the plain potential-fraud
 * line when the stop is flagged by score alone (no linked alert / why).
 */
function fraudBlock(p: ReplayPoint): string {
  const sev = stopSeverity(p);
  if (p.rule_id || p.why_summary) {
    const chip = sev ? ` ${severityChipHtml(sev)}` : '';
    const head = `${escHtml(p.rule_id ?? 'FRAUD')}${chip}`;
    const why = p.why_summary
      ? `<div style="color:#7f1d1d">${escHtml(truncate(p.why_summary))}</div>`
      : '';
    const paused = p.potential_fraud
      ? '<div style="margin-top:2px">⏸ playback paused</div>'
      : '';
    return `<div style="color:#b91c1c;font-weight:600;margin-top:4px">⚠ ${head}</div>${why}${paused}`;
  }
  return p.potential_fraud
    ? '<div style="color:#b91c1c;font-weight:600;margin-top:4px">⚠ Potential fraud — playback paused</div>'
    : '';
}

function blipHtml(p: ReplayPoint, r: ReplayResponse): string {
  const makeModel = [r.make, r.model].filter(Boolean).join(' ') || '—';
  const tel = p.telephone ? `☎ ${escHtml(p.telephone)}` : 'no telephone on file';
  const odo = p.odometer_km != null ? `${p.odometer_km.toLocaleString()} km` : '—';
  const span = `${p.km_since_last != null ? p.km_since_last.toLocaleString() : '—'} km since last · ${
    p.cumulative_km != null ? p.cumulative_km.toLocaleString() : '—'
  } km total`;
  const lkm = p.l_per_100km != null ? `${p.l_per_100km.toFixed(1)} L/100km` : '— L/100km';
  const since =
    p.hours_since_last != null
      ? `${p.hours_since_last >= 48 ? `${(p.hours_since_last / 24).toFixed(1)} days` : `${p.hours_since_last.toFixed(1)} hrs`} since last`
      : 'first stop';
  const when = new Date(p.transaction_time).toLocaleString('en-ZA', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  return `
    <div style="font-family:Inter,system-ui,sans-serif;font-size:12px;min-width:230px">
      <div style="font-weight:600;font-size:13px">${escHtml(r.registration)}</div>
      <div style="color:#64748b">${escHtml(makeModel)}</div>
      <div style="margin-top:4px;font-weight:500">${escHtml(p.touchpoint_name ?? p.kind)} · ${escHtml(p.kind)}</div>
      <div style="color:#64748b">${tel}</div>
      <hr style="border:none;border-top:1px solid #e2e8f0;margin:6px 0"/>
      <div>Odometer: <strong>${odo}</strong></div>
      <div>Span: ${span}</div>
      <div>${lkm} · ${since}</div>
      <div style="color:#94a3b8;margin-top:2px">${when}</div>
      ${fraudBlock(p)}
    </div>`;
}

export interface VehicleReplayPanelProps {
  mapRef: RefObject<FullMapHandle | null>;
  /**
   * Registration the AI asked to replay (via a grounded `replay_vehicle` map
   * action). When it changes the panel auto-selects that vehicle and loads its
   * fraud-replay — the replay is driven by Maps.tsx panel state, NOT by a
   * FullMap handle (the replay logic lives entirely in this panel).
   */
  requestedReg?: string | null;
  /**
   * Start playback automatically once `requestedReg`'s replay has loaded
   * (deep link `/maps?vehicle=…&autoplay=1`, used by the guided tour so the
   * trail is already moving when the presenter starts talking).
   *
   * Armed once per `requestedReg` and consumed on the first successful load,
   * so a later date-range refetch does NOT silently restart playback.
   */
  autoplay?: boolean;
}

export default function VehicleReplayPanel({
  mapRef,
  requestedReg,
  autoplay = false,
}: VehicleReplayPanelProps) {
  const [regInput, setRegInput] = useState('');
  const [selectedReg, setSelectedReg] = useState<string | undefined>(undefined);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(2);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);

  // Debounced typeahead over registration / make / model (wildcard ILIKE server-side).
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(regInput.trim()), 250);
    return () => clearTimeout(t);
  }, [regInput]);
  const searchQuery = useVehicleSearch(searchTerm);
  const results = searchQuery.data?.data ?? [];

  const replayQuery = useVehicleReplay(selectedReg, {
    start: from ? `${from}T00:00:00` : undefined,
    end: to ? `${to}T23:59:59` : undefined,
  });
  const replay = replayQuery.data;
  const points = useMemo<ReplayPoint[]>(() => replay?.points ?? [], [replay]);

  const blipRef = useRef<maplibregl.Marker | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const blipElRef = useRef<HTMLDivElement | null>(null);

  // Full road polyline + per-stop offsets, rebuilt when the replay changes.
  const replayPath = useMemo(
    () => (replay && points.length ? buildReplayPath(replay) : null),
    [replay, points],
  );

  // Animation state in refs (the rAF loop reads them without re-subscribing).
  const rafRef = useRef<number | null>(null);
  const curStopRef = useRef(0); // stop we're travelling FROM (the current leg)
  const legProgRef = useRef(0); // 0..1 time-progress along the current leg
  const lastTsRef = useRef(0);
  const lastTrailTsRef = useRef(0);
  const speedRef = useRef(speed);
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  const getMap = useCallback(() => {
    const m = mapRef.current?.getMap() ?? null;
    // After maplibre's map.remove() the style is torn down; getLayer/getSource
    // on such a map throws ("Cannot read properties of undefined"). Unmount
    // ordering between the map owner and this panel is not guaranteed, so a
    // destroyed map must read as "no map" to every caller (incl. cleanups).
    return m && (m as unknown as { style?: unknown }).style ? m : null;
  }, [mapRef]);

  const clearMap = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const map = getMap();
    if (map) {
      for (const id of [TRAIL_LAYER, TRAIL_HALO]) if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(TRAIL_SRC)) map.removeSource(TRAIL_SRC);
      for (const id of [STOPS_PULSE, STOPS_LAYER]) if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(STOPS_SRC)) map.removeSource(STOPS_SRC);
    }
    blipRef.current?.remove();
    blipRef.current = null;
    popupRef.current?.remove();
    popupRef.current = null;
    blipElRef.current = null;
  }, [getMap]);

  // Reveal the trail to `dist` km along the road and place the blip there; the
  // popup shows `stop` — the last fill the vehicle passed.
  const drawAt = useCallback(
    (dist: number, stop: number) => {
      const map = getMap();
      if (!map || !replay || !replayPath || points.length === 0) return;
      const tail = posAtKm(replayPath, dist);
      const v = vertexAtKm(replayPath.cum, dist);
      const coords = replayPath.path.slice(0, v + 1);
      coords.push(tail);
      const lineData = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: coords.length >= 2 ? coords : [tail, tail],
        },
      };
      const src = map.getSource(TRAIL_SRC) as maplibregl.GeoJSONSource | undefined;
      if (src) {
        src.setData(lineData as never);
      } else {
        map.addSource(TRAIL_SRC, { type: 'geojson', data: lineData as never });
        map.addLayer({
          id: TRAIL_HALO,
          type: 'line',
          source: TRAIL_SRC,
          paint: { 'line-color': '#ffffff', 'line-width': 6, 'line-opacity': 0.6 },
        });
        map.addLayer({
          id: TRAIL_LAYER,
          type: 'line',
          source: TRAIL_SRC,
          paint: { 'line-color': '#2563eb', 'line-width': 3.5 },
        });
      }
      const p = points[Math.min(stop, points.length - 1)];
      if (!blipRef.current) {
        const el = document.createElement('div');
        el.style.cssText =
          'width:18px;height:18px;border-radius:50%;border:3px solid white;' +
          'box-shadow:0 2px 6px rgba(0,0,0,0.5);cursor:pointer';
        blipElRef.current = el;
        blipRef.current = new maplibregl.Marker({ element: el }).setLngLat(tail).addTo(map);
        popupRef.current = new maplibregl.Popup({ offset: 16, closeButton: false, closeOnClick: false });
      }
      if (blipElRef.current) {
        blipElRef.current.style.background = stopColour(p);
      }
      blipRef.current.setLngLat(tail);
      popupRef.current?.setLngLat(tail).setHTML(blipHtml(p, replay)).addTo(map);
    },
    [getMap, replay, replayPath, points],
  );

  // rAF tick: drive the vehicle along the road at constant ground speed, the
  // map panning to follow; pause on a potential-fraud stop or at the end.
  const tick = useCallback(
    (ts: number) => {
      const rp = replayPath;
      const map = getMap();
      if (!rp || !map || points.length < 2) return;
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = Math.min((ts - lastTsRef.current) / 1000, 0.1);
      lastTsRef.current = ts;

      // Each leg takes a fixed wall-clock time, regardless of its distance.
      const secs = SECONDS_PER_LEG[speedRef.current] ?? 10;
      const prevLeg = curStopRef.current;
      let leg = prevLeg;
      let prog = legProgRef.current + dt / secs;

      let paused = false;
      let ended = false;
      while (prog >= 1) {
        const arrived = leg + 1;
        if (arrived >= points.length - 1) {
          leg = points.length - 1;
          prog = 0;
          ended = true;
          break;
        }
        if (points[arrived].potential_fraud) {
          leg = arrived; // stop ON the fraud fill
          prog = 0;
          paused = true;
          break;
        }
        leg = arrived;
        prog -= 1;
      }
      curStopRef.current = leg;
      legProgRef.current = prog;
      if (leg !== prevLeg) setIdx(leg);

      const fromKm = rp.stopKm[leg];
      const toKm = leg + 1 < points.length ? rp.stopKm[leg + 1] : fromKm;
      const dist = ended ? rp.totalKm : fromKm + prog * (toKm - fromKm);

      const tail = posAtKm(rp, dist);
      // Move the blip every frame (cheap); redraw the trail at ~30fps (heavier).
      if (paused || ended || !blipRef.current || ts - lastTrailTsRef.current > 33) {
        drawAt(dist, leg);
        lastTrailTsRef.current = ts;
      } else {
        blipRef.current.setLngLat(tail);
      }
      map.setCenter(tail);

      if (paused || ended) {
        setPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    [replayPath, getMap, points, drawAt],
  );

  // New data → rebuild, reset to the oldest stop, focus it at street zoom.
  useEffect(() => {
    clearMap();
    if (!replay || !replayPath || points.length === 0) return;
    curStopRef.current = 0;
    legProgRef.current = 0;
    setIdx(0);
    setPlaying(false);
    drawAt(0, 0);
    const map = getMap();
    if (map)
      map.easeTo({
        center: posAtKm(replayPath, 0),
        zoom: Math.max(map.getZoom(), 15),
        duration: 600,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replay, replayPath]);

  // Autoplay one-shot. MUST be declared after the effect above, which force-
  // resets `playing` to false on every data arrival — declaring it earlier
  // would let that reset win and the trail would sit still.
  const autoplayPendingRef = useRef(false);
  useEffect(() => {
    if (!autoplayPendingRef.current) return;
    // A single-point trail has nothing to animate; leave it parked.
    if (!replayPath || points.length < 2) return;
    autoplayPendingRef.current = false;
    setPlaying(true);
  }, [replayPath, points.length]);

  // Every stop drawn on the trail, flagged ones in their severity colour at a
  // larger radius; Critical/High additionally get a pulsing halo so they read
  // at a glance during playback. Declared AFTER the reset effect so the dots
  // are added on top of the trail line (MapLibre stacks by insertion order),
  // and after that effect's clearMap() has torn the previous run down.
  useEffect(() => {
    const map = getMap();
    if (!map || points.length === 0) return;

    // Defensive: a re-run that skipped teardown (style reload, hot update)
    // would otherwise throw on a duplicate source id.
    for (const id of [STOPS_PULSE, STOPS_LAYER]) if (map.getLayer(id)) map.removeLayer(id);
    if (map.getSource(STOPS_SRC)) map.removeSource(STOPS_SRC);

    map.addSource(STOPS_SRC, { type: 'geojson', data: buildStopsGeoJson(points) as never });
    map.addLayer({
      id: STOPS_PULSE,
      type: 'circle',
      source: STOPS_SRC,
      filter: PULSE_FILTER as never,
      paint: {
        'circle-color': STOP_COLOUR_EXPR as never,
        'circle-radius': 10,
        'circle-opacity': 0.25,
        'circle-blur': 0.35,
      },
    });
    map.addLayer({
      id: STOPS_LAYER,
      type: 'circle',
      source: STOPS_SRC,
      paint: {
        'circle-color': STOP_COLOUR_EXPR as never,
        'circle-radius': ['case', ['get', 'flagged'], 7, 4] as never,
        'circle-opacity': ['case', ['get', 'flagged'], 0.95, 0.7] as never,
        'circle-stroke-width': ['case', ['get', 'flagged'], 2, 1] as never,
        'circle-stroke-color': '#ffffff',
      },
    });

    // Clicking a stop scrubs the replay to it — same paused-scrub path the
    // slider uses, so the popup and camera follow without duplicate logic.
    const onClick = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      const i = e.features?.[0]?.properties?.idx;
      if (typeof i !== 'number') return;
      setPlaying(false);
      setIdx(i);
    };
    const onEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const onLeave = () => {
      map.getCanvas().style.cursor = '';
    };
    map.on('click', STOPS_LAYER, onClick);
    map.on('mouseenter', STOPS_LAYER, onEnter);
    map.on('mouseleave', STOPS_LAYER, onLeave);

    // Halo pulse — only worth a timer when there is something loud to pulse.
    const hasPulse = points.some((p) => {
      const sev = stopSeverity(p);
      return sev != null && PULSE_SEVERITIES.includes(sev);
    });
    // The interval + cleanup close over `map`; if the owner unmounts first,
    // map.remove() tears the style down and getLayer on it throws.
    const mapAlive = () => (map as unknown as { style?: unknown }).style != null;
    const timer = hasPulse
      ? window.setInterval(() => {
          if (!mapAlive() || !map.getLayer(STOPS_PULSE)) return;
          const t = (Date.now() % 1600) / 1600;
          map.setPaintProperty(STOPS_PULSE, 'circle-radius', 9 + t * 13);
          map.setPaintProperty(STOPS_PULSE, 'circle-opacity', 0.3 * (1 - t));
        }, 60)
      : null;

    return () => {
      if (timer != null) window.clearInterval(timer);
      if (!mapAlive()) return;
      map.off('click', STOPS_LAYER, onClick);
      map.off('mouseenter', STOPS_LAYER, onEnter);
      map.off('mouseleave', STOPS_LAYER, onLeave);
      for (const id of [STOPS_PULSE, STOPS_LAYER]) if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(STOPS_SRC)) map.removeSource(STOPS_SRC);
    };
  }, [points, getMap]);

  // Scrub / restart (only while paused) → jump to that stop at street zoom.
  useEffect(() => {
    if (playing || !replayPath || points.length === 0) return;
    const dist = replayPath.stopKm[idx] ?? 0;
    curStopRef.current = idx;
    legProgRef.current = 0;
    drawAt(dist, idx);
    const map = getMap();
    if (map)
      map.easeTo({
        center: posAtKm(replayPath, dist),
        zoom: Math.max(map.getZoom(), 15),
        duration: 400,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // Start/stop the rAF playback loop.
  useEffect(() => {
    if (!playing || !replayPath || points.length === 0) return;
    lastTsRef.current = 0;
    const map = getMap();
    // Pull back from extreme street zoom so the drive along the road is watchable.
    if (map && map.getZoom() > 14) map.setZoom(FOLLOW_ZOOM);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [playing, tick, replayPath, points.length]);

  // Clear overlays + stop animation when the tool unmounts (mode switch / leave).
  useEffect(() => clearMap, [clearMap]);

  const togglePlay = () => {
    if (!replayPath || points.length === 0) return;
    if (playing) {
      setPlaying(false);
      return;
    }
    if (idx >= points.length - 1) {
      curStopRef.current = 0; // restart from the beginning
      legProgRef.current = 0;
      setIdx(0);
    } else {
      curStopRef.current = idx;
    }
    setPlaying(true);
  };

  const selectVehicle = (reg: string) => {
    setRegInput(reg);
    setSearchTerm(reg);
    setSelectedReg(reg);
    setFrom(null);
    setTo(null);
    setOpen(false);
    setHighlightIdx(-1);
  };

  const clearSelection = () => {
    setRegInput('');
    setSearchTerm('');
    setSelectedReg(undefined);
    setOpen(false);
    setHighlightIdx(-1);
  };

  // AI-driven: when Maps.tsx hands us a `requestedReg` (from a grounded
  // `replay_vehicle` action) auto-select that vehicle. Deduped by value so a
  // manual re-select of the same reg isn't clobbered on every render.
  const lastRequestedRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (requestedReg && requestedReg !== lastRequestedRef.current) {
      lastRequestedRef.current = requestedReg;
      // Arm autoplay alongside the selection so it is scoped to THIS request;
      // the effect above disarms it on the first load that follows.
      if (autoplay) autoplayPendingRef.current = true;
      selectVehicle(requestedReg);
    }
    // selectVehicle is a stable in-render closure; depend only on requestedReg.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedReg]);

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[highlightIdx] ?? results[0];
      if (pick) selectVehicle(pick.registration);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const cur = points[idx];
  const atFraud = Boolean(cur?.potential_fraud);
  const curSeverity = cur ? stopSeverity(cur) : null;

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3 text-sm">
      <div>
        <div className="font-semibold">Vehicle replay</div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Search a registration and play its movement over time.
        </p>
      </div>

      <div className="relative">
        <div className="flex gap-1">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={regInput}
              onChange={(e) => {
                setRegInput(e.target.value);
                setOpen(true);
                setHighlightIdx(-1);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => window.setTimeout(() => setOpen(false), 120)}
              onKeyDown={onSearchKeyDown}
              placeholder="Search registration / make / model…"
              className="h-8 pl-7 text-xs"
              autoComplete="off"
            />
          </div>
          {selectedReg && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2"
              onClick={clearSelection}
              title="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {open && searchTerm.length >= 2 && (
          <div className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover shadow-md">
            {searchQuery.isFetching && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">Searching…</div>
            )}
            {!searchQuery.isFetching && results.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                No vehicles match “{searchTerm}”.
              </div>
            )}
            {results.map((v, i) => (
              <button
                key={v.asset_token}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectVehicle(v.registration);
                }}
                onMouseEnter={() => setHighlightIdx(i)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-xs hover:bg-accent',
                  i === highlightIdx && 'bg-accent',
                )}
              >
                <span className="font-medium">{v.registration}</span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {[v.make, v.model].filter(Boolean).join(' ')}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {replayQuery.isFetching && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading movement…
        </div>
      )}
      {replayQuery.isError && (
        <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
          {(replayQuery.error as Error).message}
        </div>
      )}
      {selectedReg && replay && points.length === 0 && !replayQuery.isFetching && (
        <div className="rounded border bg-muted/40 p-2 text-xs text-muted-foreground">
          No geolocated stops for {selectedReg} in this range.
        </div>
      )}

      {replay && points.length > 0 && (
        <>
          <div className="rounded border bg-card p-2">
            <div className="text-xs font-medium">
              {replay.registration}
              {(replay.make || replay.model) && (
                <span className="text-muted-foreground">
                  {' '}· {[replay.make, replay.model].filter(Boolean).join(' ')}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {points.length} stops · {points.filter((p) => p.potential_fraud).length} flagged
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
              From
              <input
                type="date"
                value={from ?? toDateInput(replay.period_start)}
                onChange={(e) => setFrom(e.target.value || null)}
                className="rounded border bg-background px-1.5 py-1 text-xs text-foreground"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
              To
              <input
                type="date"
                value={to ?? toDateInput(replay.period_end)}
                onChange={(e) => setTo(e.target.value || null)}
                className="rounded border bg-background px-1.5 py-1 text-xs text-foreground"
              />
            </label>
          </div>

          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2"
              title="Restart"
              onClick={() => {
                setPlaying(false);
                setIdx(0);
              }}
            >
              <SkipBack className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" className="h-8 flex-1" onClick={togglePlay}>
              {playing ? <Pause className="mr-1 h-3.5 w-3.5" /> : <Play className="mr-1 h-3.5 w-3.5" />}
              {playing ? 'Pause' : atFraud ? 'Resume' : 'Play'}
            </Button>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-muted-foreground">Speed</span>
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSpeed(s)}
                className={cn(
                  'rounded border px-1.5 py-0.5 text-[11px]',
                  speed === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
                )}
              >
                {s}×
              </button>
            ))}
          </div>

          <input
            type="range"
            min={0}
            max={points.length - 1}
            value={idx}
            onChange={(e) => {
              setPlaying(false);
              setIdx(Number(e.target.value));
            }}
            className="w-full"
          />
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            <span>
              Stop {idx + 1} / {points.length}
              {cur && <> · {new Date(cur.transaction_time).toLocaleDateString('en-ZA')}</>}
            </span>
            {curSeverity && (
              <span
                className={cn(
                  'rounded border px-1.5 py-0.5 text-[10px] font-semibold',
                  SEVERITY_BADGE_CLASS[curSeverity],
                )}
              >
                {curSeverity}
              </span>
            )}
            {cur?.rule_id && <span className="font-mono text-[10px]">{cur.rule_id}</span>}
          </div>

          {atFraud && (
            <div className="flex items-center gap-1.5 rounded border border-red-300 bg-red-50 p-2 text-[11px] text-red-800">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Potential fraud at this stop — playback paused. Press Play to continue.
            </div>
          )}
        </>
      )}

      {/* Sits below the play/speed/scrubber controls once a vehicle is loaded,
          and doubles as the entry point before one is picked. Rows load the
          vehicle through `selectVehicle` — the same path the search box uses. */}
      <HighRiskVehiclesList selectedReg={selectedReg} onSelect={selectVehicle} />
    </div>
  );
}
